import { Injectable } from '@angular/core';
import { ReportType } from 'src/app/admin/Subscription/subscription-management-model';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { DashboardModuleResponsive } from 'src/shared/models/module-info.model';
import { ModuleManagerGenericSettingsControls, ModuleManagerModuleSettings } from 'src/shared/models/module-manager/module-manager-settings.models';
import { ICvSubscriptionScheduleData, ReportSubscription } from 'src/shared/models/subscriptions.model';

@Injectable({
    providedIn: 'root'
})
export class ModuleSubscriptionService {

    constructor(private _apiService: CVApiService) { }

    public async createSubscription(newSubscription: ICvSubscriptionScheduleData): Promise<ReportSubscription> {
        try {
            const response = await this._apiService.post<ReportSubscription>("/api/v2/reports/subscriptions", newSubscription);
            if (response.status_code < 200 || response.status_code >= 300)
                throw response;
            return response.data;
        }
        catch (err) {
            console.error("Failed to create subscription");
            throw err;
        }
    }

    public async copySubscription(subscriptionId: number): Promise<ReportSubscription> {
        try {
            const response = await this._apiService.post<ReportSubscription>(`/api/v2/reports/subscriptions/copy/${subscriptionId}`);
            if (response.status_code < 200 || response.status_code >= 300)
                throw response;
            return response.data;
        }
        catch (err) {
            console.error("Failed to copy subscription");
            throw err;
        }
    }

    public async getAllSubscriptions(): Promise<ReportSubscription> {
        try {
            const response = await this._apiService.get<ReportSubscription>("api/v2/reports/subscriptions");
            if (response.status_code < 200 || response.status_code >= 300)
                throw response;
            return response.data;
        }
        catch (err) {
            console.error("Failed getting all subscriptions");
            throw err;
        }
    }

    public async getAllSubscriptionsByType(reportType: ReportType): Promise<ReportSubscription> {
        try {
            const response = await this._apiService.get<ReportSubscription>(`/api/v2/reports/${reportType}/subscriptions`);
            if (response.status_code < 200 || response.status_code >= 300)
                throw response;
            return response.data;
        }
        catch (err) {
            console.error("Failed getting all subscriptions by type");
            throw err;
        }
    }

    public async updateSubscription(modifiedSubscription: ICvSubscriptionScheduleData): Promise<ReportSubscription> {
        try {
            const response = await this._apiService
                .put<ReportSubscription>(`/api/v2/reports/subscriptions/${modifiedSubscription.subscriptionId}`,
                    modifiedSubscription);
            if (response.status_code < 200 || response.status_code >= 300)
                throw response;
            return response.data;
        }
        catch (err) {
            console.error("Failed to update subscription");
            throw err;
        }
    }

    public async deleteSubscription(subscriptionId: number): Promise<boolean> {
        try {
            const response = await this._apiService.delete(`/api/v2/reports/subscriptions/${subscriptionId}`);
            if (response.status_code < 200 || response.status_code >= 300)
                return false;
            return true;
        }
        catch (err) {
            console.error("Failed to delete subscription");
            throw err;
        }
    }

    public async getModuleForSubscription(id: number): Promise<string> {
        try {
            const response = await this._apiService.get<string>(`/api/v2/reports/subscriptions/module/${id}`);
            if (response.status_code >= 300 || response.status_code < 200)
                throw response;
            return response.data;
        }
        catch (err) {
            console.error("Failed to get widget for subscription");
            throw err;
        }
    }

    public async deleteSubscriptionModule(moduleKey: string): Promise<boolean> {
        try {
            const response = await this._apiService.delete(`/api/v2/reports/subscriptions/module/${moduleKey}`);
            if (response.status_code < 200 || response.status_code >= 300)
                return false;
            return true;
        }
        catch (err) {
            console.error("Failed to delete subscription widget");
            throw err
        }
    }

    public async getSubscribableModules(): Promise<DashboardModuleResponsive[]> {
        try {
            const response = await this._apiService.get<DashboardModuleResponsive[]>("/api/v2/modules/subscribable");
            if (response.status_code < 200 || response.status_code >= 300)
                throw response;
            return response.data;
        }
        catch (err) {
            console.error("Failed getting subscribable widgets");
            throw err;
        }
    }

    public async getModuleGenericSettings(moduleId: number, moduleKey: string): Promise<ModuleManagerGenericSettingsControls> {
        try {
            const response = await this._apiService.
                get<ModuleManagerGenericSettingsControls>(`/api/v2/genericSettings/module/${moduleId}/key/${moduleKey}`);
            if (response.status_code < 200 || response.status_code >= 300)
                throw response;
            return response.data;
        }
        catch (err) {
            console.error("Failed getting widget generic settings");
            throw err;
        }
    }

    public async applyModuleGenericSettings(moduleId: number, moduleKey: string): Promise<ModuleManagerModuleSettings> {
        try {
            const response = await this._apiService
                .put<ModuleManagerModuleSettings>(`/api/v2/reports/subscriptions/module/${moduleId}/key/${moduleKey}`);
            if (response.status_code < 200 || response.status_code >= 300)
                throw response;
            return response.data;
        }
        catch (err) {
            console.error("Failed applying widget generic settings");
        }
    }
}
